/**
 * MapPanel - World/module map for game sessions
 *
 * Per UX_DESIGN.md:
 * - Only available during game sessions
 * - Shows placeholder in lobby context
 * - Displays world map image/video from game state in session context
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Map, User, ChevronDown, ChevronUp } from 'lucide-react';
import { useSelectedCharacter } from '@/lib/stores/lobbyStore';
import { useGameStore } from '@/lib/stores/gameStore';
import ManagedVideo from '@/components/common/ManagedVideo';
import ImageModal from '@/components/common/ImageModal';
import { usePanelCollapse } from '@/lib/hooks/usePanelCollapse';

interface MapPanelProps {
  context?: 'lobby' | 'game-session';
}

export default function MapPanel({ context = 'lobby' }: MapPanelProps) {
  const selectedCharacter = useSelectedCharacter();
  const [isExpanded, toggleExpanded] = usePanelCollapse('map');
  const mapImageUrl = useGameStore((s) => s.mapImageUrl);
  const mapVideoUrl = useGameStore((s) => s.mapVideoUrl);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const hasMap = mapVideoUrl || mapImageUrl;

  const openOverlay = useCallback(() => {
    if (hasMap && !mapError) setOverlayOpen(true);
  }, [hasMap, mapError]);

  const closeOverlay = useCallback(() => setOverlayOpen(false), []);

  // Handle cached images that complete before React attaches onLoad
  const mapImgRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) {
      setMapLoaded(true);
    }
  }, []);

  useEffect(() => {
    setMapLoaded(false);
    setMapError(false);
  }, [mapImageUrl, mapVideoUrl]);

  // In lobby context, show placeholder
  if (context === 'lobby') {
    // No character selected in lobby
    if (!selectedCharacter) {
      return (
        <Card className="h-full">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <Map className="h-4 w-4" />
                Map
              </CardTitle>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={toggleExpanded}
                aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardHeader>
          {isExpanded && (
            <CardContent className="flex flex-col items-center justify-center text-muted-foreground h-48">
              <User className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-sm text-center">Select a character first</p>
            </CardContent>
          )}
        </Card>
      );
    }

    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Map className="h-4 w-4" />
              Map
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent className="flex flex-col items-center justify-center text-muted-foreground h-48">
            <Map className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm text-center">Not in a game session</p>
            <p className="text-xs text-center mt-2 max-w-[200px]">
              The map will appear when you enter an adventure with your party.
            </p>
          </CardContent>
        )}
      </Card>
    );
  }

  // Game session context - display map from game state
  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Map className="h-4 w-4" />
              Campaign Map
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={toggleExpanded}
              aria-label={isExpanded ? 'Collapse panel' : 'Expand panel'}
            >
              {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
            <div
              className={`relative rounded-lg overflow-hidden flex items-center justify-center ${hasMap && !mapError ? 'cursor-pointer' : ''}`}
              onClick={openOverlay}
            >
              {hasMap && !mapError ? (
                mapVideoUrl ? (
                  <ManagedVideo
                    src={mapVideoUrl}
                    className={`w-full transition-opacity duration-300 ${
                      mapLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoadedData={() => setMapLoaded(true)}
                    onCanPlay={() => setMapLoaded(true)}
                    onError={() => setMapError(true)}
                  />
                ) : (
                  <img
                    ref={mapImgRef}
                    src={mapImageUrl!}
                    alt="Campaign Map"
                    className={`w-full transition-opacity duration-300 ${
                      mapLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setMapLoaded(true)}
                    onError={() => setMapError(true)}
                  />
                )
              ) : (
                <div className="text-center text-muted-foreground">
                  <Map className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No map available</p>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      <ImageModal
        isOpen={overlayOpen}
        onClose={closeOverlay}
        imageUrl={mapImageUrl ?? undefined}
        videoUrl={mapVideoUrl ?? undefined}
        altText="Campaign Map"
      />
    </>
  );
}
